import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close,
  Hotel,
  Bed,
  AttachMoney,
  CheckCircle,
  Cancel,
  CleaningServices,
  Build,
  Wifi,
  Tv,
  AcUnit,
  Bathtub,
  LocalParking,
  FreeBreakfast,
  Balcony,
  Security,
  History,
  Info,
} from '@mui/icons-material';

const statusOptions = ['Available', 'Occupied', 'Cleaning', 'Maintenance', 'Out of Service'];
const roomTypes = ['Standard', 'Deluxe', 'Suite', 'Presidential'];
const bedTypes = ['Single', 'Double', 'Queen', 'King', 'Twin'];

const amenityOptions = [
  { value: 'Wifi', label: 'WiFi', icon: <Wifi fontSize="small" /> },
  { value: 'TV', label: 'TV', icon: <Tv fontSize="small" /> },
  { value: 'AC', label: 'Air Conditioning', icon: <AcUnit fontSize="small" /> },
  { value: 'Bathtub', label: 'Bathtub', icon: <Bathtub fontSize="small" /> },
  { value: 'Parking', label: 'Parking', icon: <LocalParking fontSize="small" /> },
  { value: 'Breakfast', label: 'Breakfast', icon: <FreeBreakfast fontSize="small" /> },
  { value: 'Balcony', label: 'Balcony', icon: <Balcony fontSize="small" /> },
  { value: 'Safe', label: 'Safe', icon: <Security fontSize="small" /> },
];

export default function ManageRoomDialog({ open, onClose, room, onSave, mode = 'edit' }) {
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    number: '',
    type: 'Standard',
    floor: 1,
    status: 'Available',
    price: '',
    capacity: 2,
    bedType: 'Double',
    amenities: [],
    description: '',
    maintenanceNotes: '',
    lastCleaned: '',
    lastMaintenance: '',
  });

  const [errors, setErrors] = useState({});
  const [maintenanceHistory] = useState([
    { date: '2025-11-10', type: 'Cleaning', notes: 'Deep cleaning completed', staff: 'Maria Johnson' },
    { date: '2025-11-05', type: 'Maintenance', notes: 'Fixed AC unit', staff: 'John Smith' },
    { date: '2025-11-01', type: 'Inspection', notes: 'Routine inspection passed', staff: 'Sarah Lee' },
  ]);

  useEffect(() => {
    if (room && mode === 'edit') {
      setFormData({
        number: room.number || '',
        type: room.type || 'Standard',
        floor: room.floor || 1,
        status: room.status || 'Available',
        price: typeof room.price === 'string' ? room.price.replace('$', '') : room.price || '',
        capacity: room.capacity || 2,
        bedType: room.bedType || 'Double',
        amenities: room.amenities || [],
        description: room.description || '',
        maintenanceNotes: room.maintenanceNotes || '',
        lastCleaned: room.lastCleaned || '',
        lastMaintenance: room.lastMaintenance || '',
      });
    }
  }, [room, mode]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleAmenityToggle = (amenity) => {
    const currentAmenities = [...formData.amenities];
    const index = currentAmenities.indexOf(amenity);
    
    if (index > -1) {
      currentAmenities.splice(index, 1);
    } else {
      currentAmenities.push(amenity);
    }
    
    handleChange('amenities', currentAmenities);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.number.trim()) {
      newErrors.number = 'Room number is required';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (!formData.capacity || formData.capacity < 1) {
      newErrors.capacity = 'Valid capacity is required';
    }
    if (!formData.floor || formData.floor < 1) {
      newErrors.floor = 'Valid floor number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      setTabValue(0); // Go back to basic info tab if there are errors
      return;
    }

    const roomData = {
      ...formData,
      price: `$${parseFloat(formData.price).toFixed(0)}`,
      id: room?.id || `R${formData.number}`,
    };

    onSave(roomData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      number: '',
      type: 'Standard',
      floor: 1,
      status: 'Available',
      price: '',
      capacity: 2,
      bedType: 'Double',
      amenities: [],
      description: '',
      maintenanceNotes: '',
      lastCleaned: '',
      lastMaintenance: '',
    });
    setErrors({});
    setTabValue(0);
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Occupied': return 'error';
      case 'Cleaning': return 'warning';
      case 'Maintenance': return 'default';
      case 'Out of Service': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Available': return <CheckCircle />;
      case 'Occupied': return <Cancel />;
      case 'Cleaning': return <CleaningServices />;
      case 'Maintenance': return <Build />;
      case 'Out of Service': return <Cancel />;
      default: return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Hotel color="primary" />
            <Box>
              <Typography variant="h6">
                {mode === 'add' ? 'Add New Room' : `Manage Room ${room?.number}`}
              </Typography>
              {mode === 'edit' && room && (
                <Chip
                  label={room.status}
                  size="small"
                  color={getStatusColor(room.status)}
                  icon={getStatusIcon(room.status)}
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<Info />} label="Basic Info" iconPosition="start" />
          <Tab icon={<CleaningServices />} label="Status & Maintenance" iconPosition="start" />
          {mode === 'edit' && <Tab icon={<History />} label="History" iconPosition="start" />}
        </Tabs>
      </Box>

      <DialogContent dividers>
        {/* Tab 1: Basic Information */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Room Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Room Number"
                value={formData.number}
                onChange={(e) => handleChange('number', e.target.value)}
                error={!!errors.number}
                helperText={errors.number}
                required
                disabled={mode === 'edit'}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Room Type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                {roomTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Floor Number"
                type="number"
                value={formData.floor}
                onChange={(e) => handleChange('floor', parseInt(e.target.value) || 1)}
                error={!!errors.floor}
                helperText={errors.floor}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price per Night"
                type="number"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                error={!!errors.price}
                helperText={errors.price}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guest Capacity"
                type="number"
                value={formData.capacity}
                onChange={(e) => handleChange('capacity', parseInt(e.target.value) || 1)}
                error={!!errors.capacity}
                helperText={errors.capacity}
                inputProps={{ min: 1, max: 10 }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Bed Type"
                value={formData.bedType}
                onChange={(e) => handleChange('bedType', e.target.value)}
              >
                {bedTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Amenities
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormGroup>
                  <Grid container spacing={2}>
                    {amenityOptions.map((amenity) => (
                      <Grid item xs={6} sm={4} md={3} key={amenity.value}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.amenities.includes(amenity.value)}
                              onChange={() => handleAmenityToggle(amenity.value)}
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              {amenity.icon}
                              <Typography variant="body2">{amenity.label}</Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Room Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Add a description of the room features, view, etc..."
              />
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ bgcolor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Preview
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Bed fontSize="small" />
                    <Typography variant="body2">
                      {formData.bedType} bed • Up to {formData.capacity} guests
                    </Typography>
                  </Box>
                  {formData.amenities.length > 0 && (
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                      {formData.amenities.map((amenity) => (
                        <Chip key={amenity} label={amenity} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 2: Status & Maintenance */}
        {tabValue === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Room Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Current Status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(status)}
                      {status}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Alert severity={formData.status === 'Available' ? 'success' : 'warning'}>
                {formData.status === 'Available' && 'Room is ready for booking'}
                {formData.status === 'Occupied' && 'Room is currently occupied'}
                {formData.status === 'Cleaning' && 'Room is being cleaned'}
                {formData.status === 'Maintenance' && 'Room requires maintenance'}
                {formData.status === 'Out of Service' && 'Room is not available'}
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Maintenance Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Cleaned"
                type="date"
                value={formData.lastCleaned}
                onChange={(e) => handleChange('lastCleaned', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Maintenance"
                type="date"
                value={formData.lastMaintenance}
                onChange={(e) => handleChange('lastMaintenance', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Maintenance Notes"
                multiline
                rows={4}
                value={formData.maintenanceNotes}
                onChange={(e) => handleChange('maintenanceNotes', e.target.value)}
                placeholder="Add any maintenance issues, repairs needed, or important notes..."
              />
            </Grid>

            {formData.status === 'Maintenance' && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<Build />}>
                  This room is marked for maintenance. Please update the status once repairs are completed.
                </Alert>
              </Grid>
            )}

            {formData.status === 'Cleaning' && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<CleaningServices />}>
                  This room is scheduled for cleaning. Mark as Available once cleaning is complete.
                </Alert>
              </Grid>
            )}
          </Grid>
        )}

        {/* Tab 3: History (only in edit mode) */}
        {tabValue === 2 && mode === 'edit' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Maintenance & Cleaning History
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <List>
                {maintenanceHistory.map((entry, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        {entry.type === 'Cleaning' ? <CleaningServices color="primary" /> : <Build color="action" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1" fontWeight="bold">
                              {entry.type}
                            </Typography>
                            <Chip label={entry.date} size="small" variant="outlined" />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2">{entry.notes}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Staff: {entry.staff}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < maintenanceHistory.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Quick Stats
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total Cleanings
                      </Typography>
                      <Typography variant="h6">
                        {maintenanceHistory.filter(h => h.type === 'Cleaning').length}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total Maintenance
                      </Typography>
                      <Typography variant="h6">
                        {maintenanceHistory.filter(h => h.type === 'Maintenance').length}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {formData.status === 'Cleaning' && mode === 'edit' && (
          <Button
            variant="outlined"
            color="success"
            onClick={() => {
              handleChange('status', 'Available');
              handleSave();
            }}
            startIcon={<CheckCircle />}
          >
            Mark as Clean
          </Button>
        )}
        {formData.status === 'Maintenance' && mode === 'edit' && (
          <Button
            variant="outlined"
            color="success"
            onClick={() => {
              handleChange('status', 'Available');
              handleSave();
            }}
            startIcon={<CheckCircle />}
          >
            Mark as Fixed
          </Button>
        )}
        <Button variant="contained" onClick={handleSave}>
          {mode === 'add' ? 'Add Room' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
